import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "snapgrad_search_context";

const SearchContext = createContext(null);

const parseStoredContext = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.lat !== "number" || typeof parsed.lng !== "number") return null;
    return parsed;
  } catch (error) {
    return null;
  }
};

const parseUrlContext = () => {
  if (typeof window === "undefined") return null;
  try {
    const url = new URL(window.location.href);
    const lat = Number(url.searchParams.get("lat"));
    const lng = Number(url.searchParams.get("lng"));
    const label = url.searchParams.get("label") || "";
    const source = url.searchParams.get("source") || "";
    const updatedAt = url.searchParams.get("updatedAt") || "";
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return {
      label,
      lat,
      lng,
      source,
      updatedAt,
    };
  } catch (error) {
    return null;
  }
};

const persistUrlContext = (context) => {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    if (!context) {
      ["lat", "lng", "label", "source", "updatedAt"].forEach((key) =>
        params.delete(key)
      );
    } else {
      params.set("lat", String(context.lat));
      params.set("lng", String(context.lng));
      params.set("label", context.label || "");
      params.set("source", context.source || "");
      params.set("updatedAt", context.updatedAt || "");
    }
    url.search = params.toString();
    window.history.replaceState({}, "", url.toString());
  } catch (error) {
    // ignore persistence errors
  }
};

export const SearchContextProvider = ({ children }) => {
  const initialContext = useMemo(
    () => parseUrlContext() || parseStoredContext(),
    []
  );
  const [searchContext, setSearchContextState] = useState(initialContext);

  const setSearchContext = useCallback((nextContext) => {
    setSearchContextState(nextContext);
    if (typeof window !== "undefined") {
      if (nextContext) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextContext));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    }
    persistUrlContext(nextContext);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (searchContext) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(searchContext));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
    persistUrlContext(searchContext);
  }, [searchContext]);

  const value = useMemo(
    () => ({
      searchContext,
      setSearchContext,
    }),
    [searchContext, setSearchContext]
  );

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
};

export const useSearchContext = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error("useSearchContext must be used within SearchContextProvider");
  }
  return context;
};
